import { useEffect, useState } from "react"

import { IRIS_EVENTS, initialIrisState, applyIrisEvent, readDsbState, withSetup } from "Layout/irisState"
import { widgetLoadFailed } from "Layout/PmsAgentWidget"

/**
 * The state of Iris for whatever launches her — header button today, maybe the side menu
 * tomorrow: `{ ready, open, busy, unread, offline }`, with no idea of where it is painted.
 *
 * Seeded from `DSB.state()` when the widget handle already exists (the script is async: it
 * may have mounted before the launcher did, or not yet), then repainted by every `dsb:*`
 * event of widget 0.107.0 (`widget-integration.md` § 5). `offline` is ours: set by
 * `dsb:error` when the agent was never reached, cleared by the next `dsb:idle`/`dsb:answer`.
 *
 * `enabled` false subscribes to nothing (the launcher renders only for the widget's users).
 * `setup` (CU-869fkmt6r) comes from the saved profile, not the widget, and is folded in as it changes.
 * A widget the host already gave up on (`iris:load-failed`, CU-869fkdhjv phase 3) seeds `offline`, so an
 * "Ask Iris" mounted later says "not available", not "starting".
 */
export default function useIrisState(enabled = true, { setup = false } = {}) {
	const [state, setState] = useState(() => initialIrisState(window.DSB, { setup, loadFailed: widgetLoadFailed() }))

	useEffect(() => { setState(s => withSetup(s, setup)) }, [setup])

	useEffect(() => {
		if (!enabled) return undefined
		// the handle may have mounted between the first render and this effect
		const seed = readDsbState(window.DSB)
		if (seed) setState(s => ({ ...s, ...seed }))
		// `dsb:ready` carries the DSB object, not the three fields: read them from its readers
		const paint = e => setState(s => applyIrisEvent(s, e.type, e.type === 'dsb:ready' ? readDsbState(window.DSB) : e.detail))
		IRIS_EVENTS.forEach(type => document.addEventListener(type, paint))
		return () => IRIS_EVENTS.forEach(type => document.removeEventListener(type, paint))
	}, [enabled])

	return state
}
